import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { analyticsAPI, coursesAPI, enrollmentsAPI } from '../lib/api';
import DailyQuestionPopup from '../components/DailyQuestionPopup';
import axios from 'axios';
import {
  BookOpen,
  Trophy,
  Award,
  TrendingUp,
  Clock,
  ChevronRight,
  Sparkles,
  Target,
  Flame,
  Star,
  Quote,
  RefreshCw,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Get time-based greeting
const getTimeBasedGreeting = (t) => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) {
    return t('dashboard.goodMorning', 'Good Morning');
  } else if (hour >= 12 && hour < 17) {
    return t('dashboard.goodAfternoon', 'Good Afternoon');
  } else if (hour >= 17 && hour < 21) {
    return t('dashboard.goodEvening', 'Good Evening');
  } else {
    return t('dashboard.goodNight', 'Good Night');
  }
};

// Check if user is first time login (using is_first_login from backend)
const isFirstTimeLogin = (user) => {
  if (!user) return false;
  
  // Use the flag from backend if available
  if (typeof user.is_first_login === 'boolean') {
    return user.is_first_login;
  }
  
  // Fallback: check login_count
  if (user.login_count !== undefined) {
    return user.login_count <= 1;
  }
  
  return false;
};

export const Dashboard = () => {
  const { user } = useAuth();
  const { branding } = useBranding();
  const { t, i18n } = useTranslation();
  const [analytics, setAnalytics] = useState(null);
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiQuote, setAiQuote] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [showDailyQuestion, setShowDailyQuestion] = useState(true);

  const fetchAiQuote = async () => {
    setLoadingQuote(true);
    try {
      const token = localStorage.getItem('token');
      const currentLang = i18n.language || 'en';
      const response = await axios.get(`${API_URL}/ai/motivational-quote?lang=${currentLang}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAiQuote(response.data.quote);
    } catch (error) {
      console.error('Failed to fetch AI quote:', error);
      setAiQuote(t('dashboard.motivationalQuote', "Every expert was once a beginner. Keep learning, keep growing!"));
    } finally {
      setLoadingQuote(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [analyticsRes, coursesRes, enrollmentsRes] = await Promise.all([
          analyticsAPI.getDashboard(),
          coursesAPI.getAll(),
          enrollmentsAPI.getAll(),
        ]);
        setAnalytics(analyticsRes.data);
        // Handle both array and object response formats for courses
        const coursesData = coursesRes.data?.courses || coursesRes.data || [];
        setCourses(Array.isArray(coursesData) ? coursesData : []);
        setEnrollments(enrollmentsRes.data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    fetchAiQuote();
  }, []);

  const enrolledCourseIds = enrollments.map(e => e.course_id);
  const inProgressCourses = enrollments
    .filter(e => e.status === 'in_progress')
    .map(e => ({
      ...e,
      course: courses.find(c => c.id === e.course_id)
    }))
    .filter(e => e.course);

  const recommendedCourses = courses
    .filter(c => !enrolledCourseIds.includes(c.id))
    .slice(0, 3);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-32 w-full" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28" />)}
          </div>
          <Skeleton className="h-64 w-full" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Daily Question Popup - shown on login */}
      {showDailyQuestion && (
        <DailyQuestionPopup onClose={() => setShowDailyQuestion(false)} />
      )}
      
      <div className="space-y-6 pb-20 lg:pb-0" data-testid="dashboard">
        {/* Welcome Banner */}
        <Card 
          className="overflow-hidden border-0 text-white"
          style={{ background: `linear-gradient(90deg, ${branding.primary_color || '#2563EB'} 0%, ${branding.primary_color || '#2563EB'}cc 100%)` }}
          data-testid="welcome-banner"
        >
          <CardContent className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-white/80 text-sm mb-1">{getTimeBasedGreeting(t)}</p>
                <h1 className="text-2xl md:text-3xl font-bold mb-2">
                  {isFirstTimeLogin(user) 
                    ? t('dashboard.welcome', 'Welcome')
                    : t('dashboard.welcomeBack', 'Welcome Back')
                  }, {user?.name?.split(' ')[0]}! 👋
                </h1>
                <p className="text-white/80">
                  {inProgressCourses.length > 0
                    ? `${inProgressCourses.length} ${t('courses.inProgress')}`
                    : t('dashboard.continuelearning')}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur">
                  <Flame className="w-5 h-5 text-amber-300" />
                  <span className="font-semibold">{t('dashboard.learningStreak')}!</span>
                </div>
                <Button
                  asChild
                  variant="secondary"
                  className="rounded-full bg-white hover:bg-white/90"
                  style={{ color: branding.primary_color || '#2563EB' }}
                  data-testid="continue-learning-btn"
                >
                  <Link to="/courses">
                    {t('dashboard.continuelearning')}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* AI Motivational Quote */}
        <Card className="border-l-4 border-l-primary bg-gradient-to-r from-primary/5 to-transparent" data-testid="ai-quote-card">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-start gap-4">
              <div className="p-2 rounded-full bg-primary/10 shrink-0">
                <Quote className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-muted-foreground mb-1">{t('dashboard.motivationalQuote')}</p>
                {loadingQuote ? (
                  <Skeleton className="h-6 w-3/4" />
                ) : (
                  <p className="text-base md:text-lg italic text-foreground">
                    "{aiQuote}"
                  </p>
                )}
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={fetchAiQuote}
                disabled={loadingQuote}
                className="shrink-0"
                data-testid="refresh-quote-btn"
              >
                <RefreshCw className={`w-4 h-4 ${loadingQuote ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="card-hover" data-testid="stat-points">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('dashboard.totalPoints')}</p>
                  <p className="text-2xl md:text-3xl font-bold text-amber-500">
                    {analytics?.total_points?.toLocaleString() || 0}
                  </p>
                </div>
                <div className="p-3 rounded-full gold-gradient">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="stat-completed">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('courses.completed')}</p>
                  <p className="text-2xl md:text-3xl font-bold text-green-500">
                    {analytics?.completed_courses || 0}
                  </p>
                </div>
                <div className="p-3 rounded-full bg-green-500/10">
                  <Trophy className="w-5 h-5 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="stat-in-progress">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('courses.inProgress')}</p>
                  <p className="text-2xl md:text-3xl font-bold text-blue-500">
                    {analytics?.in_progress_courses || 0}
                  </p>
                </div>
                <div className="p-3 rounded-full bg-blue-500/10">
                  <BookOpen className="w-5 h-5 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="stat-badges">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('dashboard.badgesEarned')}</p>
                  <p className="text-2xl md:text-3xl font-bold text-purple-500">
                    {analytics?.total_badges || 0}
                  </p>
                </div>
                <div className="p-3 rounded-full bg-purple-500/10">
                  <Award className="w-5 h-5 text-purple-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Continue Learning Section */}
        {inProgressCourses.length > 0 && (
          <Card data-testid="continue-learning-section">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                {t('dashboard.continuelearning')}
              </CardTitle>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/courses">{t('common.viewAll')}</Link>
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {inProgressCourses.slice(0, 3).map((enrollment) => (
                  <Link
                    key={enrollment.id}
                    to={`/courses/${enrollment.course_id}`}
                    className="flex items-center gap-4 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
                    data-testid={`course-progress-${enrollment.course_id}`}
                  >
                    <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={enrollment.course?.thumbnail || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?q=80&w=200&auto=format&fit=crop'}
                        alt={enrollment.course?.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate">{enrollment.course?.title}</h3>
                      <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        <span>{enrollment.course?.duration_minutes} {t('common.minutes')}</span>
                        <span>•</span>
                        <span>{Math.round(enrollment.progress)}% complete</span>
                      </div>
                      <Progress value={enrollment.progress} className="h-2 mt-2" />
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recommended Courses */}
        {recommendedCourses.length > 0 && (
          <Card data-testid="recommended-courses-section">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-amber-500" />
                {t('dashboard.recommendedForYou', 'Recommended for You')}
              </CardTitle>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/courses">{t('dashboard.browseAll', 'Browse All')}</Link>
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {recommendedCourses.map((course) => (
                  <Link
                    key={course.id}
                    to={`/courses/${course.id}`}
                    className="group rounded-xl overflow-hidden border bg-card card-hover"
                    data-testid={`recommended-course-${course.id}`}
                  >
                    <div className="aspect-video relative overflow-hidden">
                      <img
                        src={course.thumbnail || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?q=80&w=400&auto=format&fit=crop'}
                        alt={course.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-2 right-2">
                        <Badge variant="secondary" className="gold-gradient text-white border-0">
                          +{course.points_reward} pts
                        </Badge>
                      </div>
                    </div>
                    <div className="p-4">
                      <Badge variant="outline" className="mb-2 capitalize">
                        {course.category}
                      </Badge>
                      <h3 className="font-semibold line-clamp-2 group-hover:text-primary transition-colors">
                        {course.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        <span>{course.duration_minutes} min</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Button
            asChild
            variant="outline"
            className="h-auto py-6 flex-col gap-2"
            data-testid="quick-action-leaderboard"
          >
            <Link to="/leaderboard">
              <Trophy className="w-6 h-6 text-amber-500" />
              <span>{t('nav.leaderboard', 'Leaderboard')}</span>
            </Link>
          </Button>
          <Button
            asChild
            variant="outline"
            className="h-auto py-6 flex-col gap-2"
            data-testid="quick-action-achievements"
          >
            <Link to="/achievements">
              <Award className="w-6 h-6 text-purple-500" />
              <span>{t('nav.achievements', 'Achievements')}</span>
            </Link>
          </Button>
          {user?.role === 'admin' && (
            <Button
              asChild
              variant="outline"
              className="h-auto py-6 flex-col gap-2"
              data-testid="quick-action-discussions"
            >
              <Link to="/discussions">
                <TrendingUp className="w-6 h-6 text-blue-500" />
                <span>{t('nav.discussions', 'Discussions')}</span>
              </Link>
            </Button>
          )}
          <Button
            asChild
            variant="outline"
            className="h-auto py-6 flex-col gap-2"
            data-testid="quick-action-paths"
          >
            <Link to="/learning-paths">
              <Target className="w-6 h-6 text-green-500" />
              <span>{t('nav.learningPaths', 'Learning Paths')}</span>
            </Link>
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
